import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

function ratingColor(bucket) {
  if (bucket?.includes("5")) return "#00ff90";
  if (bucket?.includes("4")) return "#60a5fa";
  if (bucket?.includes("3")) return "#fbbf24";
  return "#f87171";
}

export default function PerformanceRadar({ loading, data, kpis }) {
  const dist        = data?.distribution  || [];
  const competencies = data?.competencies || []; // [{subject, score}]
  const totalRated  = dist.reduce((a, b) => a + (b.count || 0), 0);

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Performance Overview</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance Overview</span>
        {kpis?.avgRating && (
          <span className="text-sm font-bold text-[#00ff90]">{kpis.avgRating} avg</span>
        )}
      </div>
      <div className="p-5 space-y-5">

        {/* Stat chips */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Avg Rating",    value: kpis?.avgRating,            color: "#00ff90"  },
            { label: "At Risk",       value: kpis?.atRisk,               color: "#f87171"  },
            { label: "Goals Done %",  value: kpis?.goalsCompletionPct != null ? `${kpis.goalsCompletionPct}%` : "—", color: "#60a5fa" },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
              <p className="text-sm font-bold" style={{ color }}>{value ?? "—"}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Rating distribution bar */}
        {dist.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Rating Distribution ({totalRated} rated)</p>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={dist} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.35)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v} employees`, "Count"]} />
                <Bar dataKey="count" radius={[4,4,0,0]}>
                  {dist.map((d, i) => <Cell key={i} fill={ratingColor(d.bucket)} fillOpacity={0.8} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Competency radar if available */}
        {competencies.length >= 3 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Org Competency Averages</p>
            <ResponsiveContainer width="100%" height={160}>
              <RadarChart data={competencies}>
                <PolarGrid stroke="rgba(255,255,255,0.06)" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
                <Radar dataKey="score" stroke="#00ff90" fill="#00ff90" fillOpacity={0.12} strokeWidth={2} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v}/5`, "Avg"]} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}